import Button from '@/features/shared/Button';
import { FacetFilters } from '@/features/shared/ElasticComponent';
import ElasticComponentContext, {
  FacetsType,
} from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { MuiAppBar, MuiBox, MuiTab, MuiTabs, MuiTypography } from '@/features/shared/material-ui';
import { CUSTOM_FACETS, Theme } from '@/utils/constants';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import React, { memo, PropsWithChildren, useContext, useMemo, useState } from 'react';
import { TabFilterProps } from './TabFilter.interface';
import useStyles from './TabFilter.styles';

type LinksType = { links: LinkType[]; label: string };

function a11yProps(index: any) {
  return {
    id: `scrollable-force-tab-${index}`,
    'aria-controls': `scrollable-force-tabpanel-${index}`,
  };
}

function TabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-force-tabpanel-${index}`}
      aria-labelledby={`scrollable-force-tab-${index}`}
      {...other}
    >
      {value === index && <MuiBox>{children}</MuiBox>}
    </div>
  );
}

const TabFilter = ({
  theme = Theme.light,
  hasTitle = true,
  size,
  renderLabel,
  onChange = () => null,
  labels,
  links,
  linksTitle,
}: PropsWithChildren<TabFilterProps>) => {
  const classes = useStyles({});
  const { facets } = useContext(ElasticComponentContext);
  const [activeTab, setActiveTab] = useState<null | number>(0);
  const [activeTabPanel, setActiveTabPanel] = useState<null | number>(-1);

  const handleClick = (nextValue: number) => {
    setActiveTabPanel(nextValue === activeTabPanel ? -1 : nextValue);
    setActiveTab(nextValue);
  };

  const customFacets = useMemo(
    () =>
      facets
        .filter((item) => CUSTOM_FACETS.has(item.name))
        .map((item) => {
          if (/is_genetec_certified/.test(item?.name))
            return {
              ...item,
              label: labels?.partner_certified || 'Partner (Certified)',
              options: item.options.filter((_item) => /true/.test(_item.value)),
            };

          return { ...item };
        }),
    [facets, labels?.partner_certified],
  );
  const discoverLinks = useMemo(() => [{ label: linksTitle, links }], [links, linksTitle]);
  const otherFacets = useMemo(
    () => facets.filter((item) => !CUSTOM_FACETS.has(item.name)),
    [facets],
  );

  const allFacets = useMemo(
    () => [...customFacets, ...discoverLinks, ...otherFacets] as (FacetsType & LinksType)[],
    [discoverLinks, customFacets, otherFacets],
  );

  const rootClasses = clsx(classes.root, `${theme}-theme`, size === 'small' && 'typography-small');
  return (
    <div className={rootClasses}>
      <MuiAppBar position="static" color="default" className={classes.filters} elevation={0}>
        {hasTitle && (
          <MuiTypography variant="overline" className="title" component="div">
            {labels?.filters || 'Filters'}
          </MuiTypography>
        )}
        <MuiTabs
          className={classes.tabs}
          value={activeTab}
          variant="scrollable"
          scrollButtons="on"
          indicatorColor="primary"
          textColor="inherit"
          aria-label="scrollable force tabs example"
        >
          {allFacets.map((item, idx: number) => (
            <MuiTab
              className={classes.tab}
              onClick={() => handleClick(idx)}
              label={
                <span>
                  {item.label}
                  <ExpandMoreIcon className={classes.tabIcon} fontSize="small" />
                </span>
              }
              // eslint-disable-next-line react/no-array-index-key
              key={`${item.name}_${idx}`}
              {...a11yProps(idx)}
            />
          ))}
        </MuiTabs>
      </MuiAppBar>
      {allFacets.length > 0 &&
        allFacets.map((item, idx: number) => (
          <TabPanel
            className={classes.tabPanel}
            value={activeTabPanel}
            index={idx}
            // eslint-disable-next-line react/no-array-index-key
            key={`${item.name}_${idx}`}
          >
            {item.links && (
              <div className={classes.links}>
                {item.links.map((link, lIdx) => (
                  <Button
                    // eslint-disable-next-line react/no-array-index-key
                    key={`link_${lIdx}_${idx}`}
                    variant="link"
                    link={link}
                    className={classes.link}
                  />
                ))}
              </div>
            )}
            {item.options && CUSTOM_FACETS.has(item.name) && (
              <FacetFilters
                facet={item.name}
                options={item.options.filter((_item) => /true|byGenetec/.test(_item.value))}
                onChange={onChange}
                renderLabel={renderLabel}
                labels={labels}
              />
            )}
            {item.options && !CUSTOM_FACETS.has(item.name) && (
              <FacetFilters
                facet={item.name}
                options={item.options}
                onChange={onChange}
                labels={labels}
              />
            )}
          </TabPanel>
        ))}
    </div>
  );
};

export default memo(TabFilter);
